/**
 * CaissePage — the cash desk. Drives the session lifecycle (open → encaisser → clôturer) and the
 * full 5-state DoD (vide / chargement / erreur / hors-ligne / sans-droit).
 *
 * - sans-droit: ProtectedRoute already blocks the route on finance.caisse.read; per-action gating
 *   (open/close/collect) is via useCan on the buttons + a ForbiddenState fallback if read is lost.
 * - hors-ligne: useUiStore(online) → OfflineState (cache-aware).
 * - chargement/erreur/vide: the "courante" query; vide = no open session → open CTA.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
  Tabs,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { FINANCE_CAPS, useCurrentSession, useLabId, useSessionStats } from "./api";
import { OpenSessionModal } from "./components/OpenSessionModal";
import { CloseSessionModal } from "./components/CloseSessionModal";
import { PaiementForm } from "./components/PaiementForm";
import { StatTile } from "./components/StatTile";
import { Money } from "./components/Money";

interface CollectHandoff {
  collectFactureId?: string;
  collectMontant?: number | null;
}

export function CaissePage() {
  const { t } = useT();
  const labId = useLabId();
  const location = useLocation();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(FINANCE_CAPS.caisseRead);
  const canOpen = useCan(FINANCE_CAPS.caisseOpen);
  const canClose = useCan(FINANCE_CAPS.caisseClose);

  // Hand-off from the facturation screen ("collect this invoice now").
  const handoff = (location.state ?? null) as CollectHandoff | null;
  const prefillFactureId = handoff?.collectFactureId;
  const prefillMontant = handoff?.collectMontant ?? null;

  const [openModal, setOpenModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [tab, setTab] = useState(prefillFactureId ? "paiement" : "session");

  // When arriving with a hand-off, land on the encaissement tab.
  useEffect(() => {
    if (prefillFactureId) setTab("paiement");
  }, [prefillFactureId]);

  const sessionQuery = useCurrentSession(labId, canRead && online);
  const session = sessionQuery.data ?? null;
  const statsQuery = useSessionStats(labId, session?.id ?? null, canRead && online && !!session);
  const stats = statsQuery.data;

  const header = (
    <header style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--nv-s-3)" }}>
        <h1 style={{ fontSize: "var(--nv-fs-h1)", margin: 0 }}>{t("finance.caisse.title")}</h1>
        {session && <Badge tone="success">{t("finance.caisse.openState")}</Badge>}
      </div>
      <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("finance.caisse.subtitle")}</p>
    </header>
  );

  // 5-state DoD, in priority order.
  if (!canRead) {
    return (
      <ForbiddenState requiredCapabilities={[FINANCE_CAPS.caisseRead]} />
    );
  }
  if (!online) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <OfflineState onRetry={() => void sessionQuery.refetch()} cached={!!session} />
      </div>
    );
  }
  if (sessionQuery.isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <LoadingState variant="table" rows={4} />
      </div>
    );
  }
  if (sessionQuery.isError) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <ErrorState
          message={sessionQuery.error.message}
          code={sessionQuery.error.code}
          onRetry={() => void sessionQuery.refetch()}
        />
      </div>
    );
  }

  // vide — no session open.
  if (!session) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <EmptyState
          glyph="▢"
          title={t("finance.caisse.none.title")}
          description={t("finance.caisse.none.desc")}
          action={
            <Button onClick={() => setOpenModal(true)} disabled={!canOpen}>
              {t("finance.caisse.open.cta")}
            </Button>
          }
        />
        <OpenSessionModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onOpened={() => {
            setOpenModal(false);
            void sessionQuery.refetch();
          }}
        />
      </div>
    );
  }

  // Session open — full desk.
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {header}

      <Tabs
        aria-label={t("finance.caisse.title")}
        value={tab}
        onChange={setTab}
        items={[
          {
            id: "session",
            label: t("finance.caisse.tab.session"),
            content: (
              <SessionPanel
                session={session}
                statsLoading={statsQuery.isLoading}
                statsError={statsQuery.isError}
                stats={stats}
                canClose={canClose}
                onRefresh={() => {
                  void sessionQuery.refetch();
                  void statsQuery.refetch();
                }}
                onClose={() => setCloseModal(true)}
              />
            ),
          },
          {
            id: "paiement",
            label: t("finance.caisse.tab.paiement"),
            content: (
              <PaiementForm
                sessionId={session.id}
                {...(prefillFactureId ? { prefillFactureId } : {})}
                {...(prefillMontant != null ? { prefillMontant } : {})}
              />
            ),
          },
        ]}
      />

      <CloseSessionModal
        open={closeModal}
        session={session}
        stats={stats}
        onClose={() => setCloseModal(false)}
        onClosed={() => {
          setCloseModal(false);
          void sessionQuery.refetch();
        }}
      />
    </div>
  );
}

interface SessionPanelProps {
  session: NonNullable<ReturnType<typeof useCurrentSession>["data"]>;
  stats: ReturnType<typeof useSessionStats>["data"];
  statsLoading: boolean;
  statsError: boolean;
  canClose: boolean;
  onRefresh: () => void;
  onClose: () => void;
}

function SessionPanel({
  session,
  stats,
  statsLoading,
  statsError,
  canClose,
  onRefresh,
  onClose,
}: SessionPanelProps) {
  const { t } = useT();

  // The only theoretical the server emits: especes_attendues (fond + encaissé − remboursé), DERIVED
  // server-side. There is no per-mode breakdown and no fond_theorique on the wire.
  const theorique = stats?.especes_attendues;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <Card
        title={`${t("finance.caisse.caisseId")} ${session?.caisse_id ?? "—"}`}
        actions={
          <div style={{ display: "flex", gap: "var(--nv-s-2)" }}>
            <Button variant="secondary" size="sm" onClick={onRefresh}>
              {t("finance.action.refresh")}
            </Button>
            <Button variant="danger" size="sm" onClick={onClose} disabled={!canClose}>
              {t("finance.caisse.close.cta")}
            </Button>
          </div>
        }
      >
        <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--nv-s-2) var(--nv-s-5)", margin: 0 }}>
          <dt style={{ color: "var(--nv-slate)" }}>{t("finance.caisse.fond")}</dt>
          <dd style={{ margin: 0 }}>
            <Money centimes={session?.fond_de_caisse} />
          </dd>
          {session?.ouverte_at && (
            <>
              <dt style={{ color: "var(--nv-slate)" }}>{t("finance.caisse.openedAt")}</dt>
              <dd className="nv-mono nv-ltr-island" style={{ margin: 0 }}>
                {session.ouverte_at}
              </dd>
            </>
          )}
        </dl>
      </Card>

      <Card title={t("finance.caisse.stats.title")}>
        {statsLoading ? (
          <LoadingState variant="table" rows={2} />
        ) : statsError ? (
          <ErrorState message={t("finance.caisse.stats.title")} onRetry={onRefresh} />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "var(--nv-s-3)",
            }}
          >
            <StatTile
              label={t("finance.caisse.stats.total")}
              centimes={stats?.total_encaisse}
              strong
            />
            <StatTile
              label={t("finance.caisse.stats.rembourse")}
              centimes={stats?.total_rembourse}
            />
            <StatTile label={t("finance.caisse.stats.theorique")} centimes={theorique} strong />
          </div>
        )}
      </Card>
    </div>
  );
}
